import { useEffect, useRef } from 'react';
import type { Dispatch } from 'react';
import {
  subscribeToNotifications,
  subscribeToNotificationUpdates,
  filterNotificationsByRole,
} from '../../services/notificationService';
import { showBrowserNotification } from '../../utils/browserNotification';
import type { ToastType } from '../ToastContext';
import { playNotificationSound } from './sound';
import { primeAudio } from '../../utils/audioPlayer';
import { buildBrowserNotification } from './browserNotification';
import type { NotificationAction, UserSession } from './types';

export function useNotificationSubscriptions(
  dispatch: Dispatch<NotificationAction>,
  session: UserSession,
  showToast: (message: string, type?: ToastType) => void
) {
  const { user, userRole } = session;

  const channelsRef = useRef<{
    insert: { unsubscribe: () => void } | null;
    update: { unsubscribe: () => void } | null;
  }>({ insert: null, update: null });
  const toastCooldownRef = useRef<Map<string, number>>(new Map());
  const soundCooldownRef = useRef<Map<string, number>>(new Map());

  // Setup realtime subscriptions
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    // Kebijakan autoplay browser: AudioContext hanya boleh bunyi setelah ada
    // interaksi. Listener PERMANEN (bukan once) — tiap gesture coba resume,
    // supaya tab yang dibuka lama/background tetap unlock saat user kembali
    // klik. Idempotent & murah (resume saat running = no-op).
    const prime = () => { void primeAudio(); };
    window.addEventListener('pointerdown', prime);
    window.addEventListener('keydown', prime);

    // Subscribe to new notifications
    const insertChannel = subscribeToNotifications(user.id, (newNotification) => {
      const filtered = filterNotificationsByRole([newNotification], userRole || '');
      if (filtered.length > 0) {
        dispatch({ type: 'ADD_NOTIFICATION', payload: newNotification });

        // ✅ Play notification sound based on type (always ON)
        const notifType = newNotification.type || 'new_order';
        // RPC fisik menulis metadata.orderId (camelCase), jalur lain
        // metadata.order_id (snake_case) — baca keduanya.
        const meta = (newNotification as any).metadata || {};
        const notifOrderId = meta.order_id ?? meta.orderId;
        playNotificationSound(notifType, notifOrderId, soundCooldownRef.current, meta.event_type);

        // 🔔 Show browser push notification (works even when tab is not active)
        const browserNotif = buildBrowserNotification(newNotification);

        // Show browser notification (won't show if permission not granted)
        showBrowserNotification(browserNotif).catch(err => {
          if (import.meta.env.DEV) {
            console.warn('[NotificationContext] Browser notification failed:', err);
          }
        });

        // Show floating toast notification (with cooldown dedup per order ID)
        const orderId = (newNotification as any).metadata?.order_id
          ?? (newNotification as any).metadata?.orderId;
        if (notifType === 'ORDER_NEW' || notifType === 'new_order' || notifType === 'DIGITAL_ORDER_NEW') {
          const now = Date.now();
          const last = orderId ? toastCooldownRef.current.get(String(orderId)) : undefined;
          if (last && now - last < 10000) return; // cooldown 10s per order ID
          if (orderId) toastCooldownRef.current.set(String(orderId), now);
          const toastMsg = newNotification.title
            ? `${newNotification.title} - ${newNotification.message || ''}`
            : newNotification.message || 'Pesanan baru masuk';
          showToast(toastMsg, 'success');
        } else {
          const toastMsg = newNotification.title
            ? `${newNotification.title} - ${newNotification.message || ''}`
            : newNotification.message || 'Notifikasi baru';
          showToast(toastMsg, 'info');
        }
      }
    });

    // Subscribe to updates (mark as read, etc)
    const updateChannel = subscribeToNotificationUpdates(user.id, (updatedNotification) => {
      dispatch({ type: 'UPDATE_NOTIFICATION', payload: updatedNotification });
    });

    channelsRef.current = { insert: insertChannel, update: updateChannel };

    // Cleanup
    return () => {
      insertChannel?.unsubscribe();
      updateChannel?.unsubscribe();
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
    };
  }, [user?.id, userRole]);
}
